import { useCallback, useEffect, useState } from "react";
import { MdNotificationsActive, MdNotificationsOff } from "react-icons/md";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import InfoBlock from "../InfoBlock/InfoBlock";
import {
  ASK_CONFIRMATION_ON_SAVE_OR_UPDATE,
  VAPID_PUBLIC_KEY,
} from "src/helpers/constants";
import apiClient from "src/helpers/api-client";
import { urlBase64ToUint8Array, showNotification } from "src/helpers/utils";
import { useTranslation } from "react-i18next";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";
import { EInfoBlockKind } from "../InfoBlock/InfoBlock.types";

const PUSH_SUBSCRIPTION_CHANGED_EVENT = "push-subscription-changed";

interface IPushSubscription {
  hideIfEnabled?: boolean;
}

const PushSubscription: React.FC<IPushSubscription> = ({
  hideIfEnabled = false,
}) => {
  const { t } = useTranslation(["modals"]);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const checkSubscription = useCallback(async () => {
    setHasChecked(false);

    if (!("serviceWorker" in navigator)) {
      setIsSubscribed(false);
      setEndpoint("");
      setHasChecked(true);
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();

    setIsSubscribed(!!existing);
    setEndpoint(existing?.endpoint || "");
    setHasChecked(true);
  }, []);

  useEffect(() => {
    checkSubscription();

    const handleSubscriptionChanged = () => {
      checkSubscription();
    };

    window.addEventListener(
      PUSH_SUBSCRIPTION_CHANGED_EVENT,
      handleSubscriptionChanged,
    );

    return () => {
      window.removeEventListener(
        PUSH_SUBSCRIPTION_CHANGED_EVENT,
        handleSubscriptionChanged,
      );
    };
  }, [checkSubscription]);

  const notifySubscriptionChanged = () => {
    window.dispatchEvent(new Event(PUSH_SUBSCRIPTION_CHANGED_EVENT));
  };

  const subscribe = async () => {
    try {
      setIsSubscribing(true);

      const registration = await navigator.serviceWorker.ready;

      const existing = await registration.pushManager.getSubscription();

      const subscription =
        existing ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }));

      const res = await apiClient.post("/notifications/subscribe", {
        subscription,
      });

      if (!res.data?.success) {
        throw new Error("Error en el servidor");
      }

      setIsSubscribed(true);
      setEndpoint(subscription.endpoint);

      notifySubscriptionChanged();

      showNotification({
        kind: "success",
        message: t("modals:notifications.push.notifications.enable.success"),
      });

      setShowSubscribeDialog(false);
    } catch (err) {
      showNotification({
        kind: "error",
        message: t("modals:notifications.push.notifications.enable.error"),
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const unsubscribe = async () => {
    try {
      setIsUnsubscribing(true);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        await apiClient.post("/notifications/unsubscribe", {
          endpoint: subscription.endpoint,
        });
      }

      setIsSubscribed(false);
      setEndpoint("");
      setShowDisableDialog(false);

      notifySubscriptionChanged();

      showNotification({
        kind: "success",
        message: t("modals:notifications.push.notifications.disable.success"),
      });
    } catch (err) {
      showNotification({
        kind: "error",
        message: t("modals:notifications.push.notifications.disable.error"),
      });
    } finally {
      setIsUnsubscribing(false);
    }
  };

  if (!hasChecked) return null;
  if (hideIfEnabled && isSubscribed) return null;

  return (
    <>
      <div className="flex w-full flex-col gap-4 md:gap-6">
        <InfoBlock
          displayMobileIcon
          title={
            isSubscribed
              ? t("modals:notifications.push.enabled_message_title")
              : t("modals:notifications.push.disabled_message_title")
          }
          message={
            isSubscribed
              ? t("modals:notifications.push.enabled_message")
              : t("modals:notifications.push.disabled_message")
          }
          buttons={[
            isSubscribed
              ? {
                  tooltip: t("modals:notifications.push.disable"),
                  icon: <MdNotificationsActive size={ICON_SIZES.SM} />,
                  onClick: async () => {
                    if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                      setShowDisableDialog(true);
                    } else {
                      await unsubscribe();
                    }
                  },
                  kind: EButtonKind.SAVE,
                  disabled: isUnsubscribing,
                  isLoading: isUnsubscribing,
                }
              : {
                  tooltip: t("modals:notifications.push.enable"),
                  icon: <MdNotificationsOff size={ICON_SIZES.SM} />,
                  onClick: async () => {
                    if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                      setShowSubscribeDialog(true);
                    } else {
                      await subscribe();
                    }
                  },
                  kind: EButtonKind.DANGER,
                  disabled: isSubscribing,
                  isLoading: isSubscribing,
                },
          ]}
          kind={isSubscribed ? EInfoBlockKind.SUCCESS : EInfoBlockKind.WARNING}
        />
      </div>

      {showSubscribeDialog && (
        <ConfirmDialog
          description={t(
            "modals:notifications.push.dialogs.enable.description",
          )}
          onClose={() => setShowSubscribeDialog(false)}
          onConfirm={subscribe}
          title={t("dialogs:titles.update")}
          isLoading={isSubscribing}
          confirmKind={EButtonKind.SAVE}
        />
      )}

      {showDisableDialog && (
        <ConfirmDialog
          description={t(
            "modals:notifications.push.dialogs.disable.description",
          )}
          onClose={() => setShowDisableDialog(false)}
          onConfirm={unsubscribe}
          title={t("dialogs:titles.update")}
          doubleConfirmRequired
          isLoading={isUnsubscribing}
          confirmKind={EButtonKind.DANGER}
        />
      )}
    </>
  );
};

export default PushSubscription;
